import { useCallback } from "react";
import { Link } from "react-router";

import { useSignUp } from "@/hooks/use-auth-client";
import { useHubSpotTracking } from "@/hooks/use-hubspot-tracking";

import { PATHS } from "@/routes/paths";

import { Button } from "@/components/ui/button/button";
import { InputPasswordField } from "@/components/ui/form/fields/input-field/input-password";
import { InputTextField } from "@/components/ui/form/fields/input-field/input-text";
import { Checkbox } from "@/components/ui/input/checkbox";

import { useLoadingScreen } from "../../side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";
import { AuthForm } from "../_components/auth-form";
import { SelectWithOptgroup } from "./_components/select-with-optgroup";
import { useSignUpForm } from "./_hooks/use-sign-up-form";

import type { SelectWithOptgroupOption } from "./_type/type";

export const HOW_DO_YOU_KNOW_DATA_ARRAY: SelectWithOptgroupOption[] = [
	{
		label: "弊社からのご案内",
		children: [
			{ key: "メルマガ", value: "メルマガ" },
			{ key: "担当者からのメール", value: "担当者からのメール" },
		],
	},
	{
		label: "SNS",
		children: [
			{ key: "X(旧 twitter)", value: "X(旧 twitter)" },
			{ key: "instagram", value: "instagram" },
			{ key: "TikTok", value: "TikTok" },
			{ key: "facebook", value: "facebook" },
		],
	},
	{
		label: "ご紹介",
		children: [
			{ key: "CHILLNN利用施設", value: "CHILLNN利用施設" },
			{ key: "建築設計事務所", value: "建築設計事務所" },
			{ key: "ホテルコンサル", value: "ホテルコンサル" },
			{ key: "水星プロデュース事業部", value: "水星プロデュース事業部" },
		],
	},
	{
		label: "他社システムからのご紹介",
		children: [
			{ key: "手間いらず", value: "手間いらず" },
			{ key: "ねっぱん！", value: "ねっぱん！" },
			{ key: "Beds24", value: "Beds24" },
			{ key: "aipass", value: "aipass" },
			{ key: "TL-Lincoln", value: "TL-Lincoln" },
		],
	},
	{
		label: "その他",
		children: [{ key: "該当なし", value: "該当なし" }],
	},
];

/** エラー枠は次の項目名に重ねず、その上まで押し下げる。 */
const SIGN_UP_ERROR_LAYOUT = "card";

export const AuthSignUp = () => {
	const {
		signUpForm,
		errors,
		passwordErrorMessage,
		disabled,

		onChangeSignUpForm,
		onInitSignUpForm,
	} = useSignUpForm();

	const handleChangeEmail = useCallback(
		(value: string) => {
			onChangeSignUpForm("email", value);
		},
		[onChangeSignUpForm],
	);

	const handleChangePassword = useCallback(
		(value: string) => {
			onChangeSignUpForm("password", value);
		},
		[onChangeSignUpForm],
	);

	const handleChangeCompanyName = useCallback(
		(value: string) => {
			onChangeSignUpForm("companyName", value);
		},
		[onChangeSignUpForm],
	);

	const handleChangePersonInChargeName = useCallback(
		(value: string) => {
			onChangeSignUpForm("personInChargeName", value);
		},
		[onChangeSignUpForm],
	);

	const handleChangeTel = useCallback(
		(value: string) => {
			onChangeSignUpForm("tel", value);
		},
		[onChangeSignUpForm],
	);

	const handleChangeHowDoYouKnow = useCallback(
		(value: string) => {
			onChangeSignUpForm("howDoYouKnow", value);
		},
		[onChangeSignUpForm],
	);

	const handleChangeAppendix = useCallback(
		(value: string) => {
			onChangeSignUpForm("appendix", value);
		},
		[onChangeSignUpForm],
	);

	const handleChangeAgree = useCallback(
		(value: boolean) => {
			onChangeSignUpForm("agree", Boolean(value));
		},
		[onChangeSignUpForm],
	);

	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();
	const { signUp } = useSignUp();
	const { track } = useHubSpotTracking();
	const handleSubmit = (_e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		showLoadingScreenCallback();
		const { email, password, ...others } = signUpForm;
		signUp({
			email,
			password,
			clientMetadata: { ...others },
			successCallback: () => {
				onInitSignUpForm();
			},
		}).finally(() => {
			hideLoadingScreenCallback();
		});

		track({
			email: email,
			path: "/auth/signUp",
		});
	};

	const canSubmit = disabled;
	const canShowAppendix = signUpForm.howDoYouKnow === "該当なし";

	return (
		<AuthForm
			formTitle="アカウントを作成"
			formDescription="CHILLNNは無料ではじめられます。初期費用はかかりません。"
			handlePrimaryButtonClick={handleSubmit}
			primaryButtonText="無料ではじめる"
			disabled={canSubmit}
			otherInfo={
				<div className="flex justify-end">
					<a href={PATHS.AUTH.SIGN_IN}>
						<Button variant="link">サインインはこちら</Button>
					</a>
				</div>
			}
		>
			<InputTextField
				label="メールアドレス"
				errorMessage={errors?.email?.at(0)}
				errorLayout={SIGN_UP_ERROR_LAYOUT}
				type="email"
				placeholder="sample@chillnn.com"
				value={signUpForm.email}
				handleChange={handleChangeEmail}
				autoComplete="email"
				name="email"
			/>
			<InputPasswordField
				autoComplete="new-password"
				label="パスワード"
				errorMessage={passwordErrorMessage}
				errorLayout={SIGN_UP_ERROR_LAYOUT}
				placeholder="••••••••"
				value={signUpForm.password}
				onChange={handleChangePassword}
				name="password"
			/>
			<InputTextField
				label="会社名"
				errorMessage={errors?.companyName?.at(0)}
				errorLayout={SIGN_UP_ERROR_LAYOUT}
				placeholder="株式会社CHILLNN"
				value={signUpForm.companyName}
				handleChange={handleChangeCompanyName}
				name="companyName"
			/>
			<InputTextField
				label="ご担当者様氏名"
				errorMessage={errors?.personInChargeName?.at(0)}
				errorLayout={SIGN_UP_ERROR_LAYOUT}
				placeholder="山田太郎"
				value={signUpForm.personInChargeName}
				handleChange={handleChangePersonInChargeName}
				name="personInChargeName"
			/>
			<InputTextField
				label="電話番号"
				type="tel"
				errorMessage={errors?.tel?.at(0)}
				errorLayout={SIGN_UP_ERROR_LAYOUT}
				placeholder="000-0000-0000"
				autoComplete="tel"
				value={signUpForm.tel}
				handleChange={handleChangeTel}
				name="tel"
			/>

			<div>
				<SelectWithOptgroup
					label="どの媒体でCHILLNNを知りましたか？"
					value={signUpForm.howDoYouKnow}
					options={HOW_DO_YOU_KNOW_DATA_ARRAY}
					errorMessage={errors?.howDoYouKnow?.at(0)}
					errorLayout={SIGN_UP_ERROR_LAYOUT}
					handleValueChange={handleChangeHowDoYouKnow}
				/>
			</div>
			{canShowAppendix && (
				<InputTextField
					label="どちらでお知りになったか教えていただけたら幸いです！"
					errorMessage={undefined}
					placeholder="備考"
					value={signUpForm.appendix}
					handleChange={handleChangeAppendix}
					name="appendix"
				/>
			)}
			<div>
				<div className="flex items-center gap-3 text-sm">
					<Checkbox checked={signUpForm.agree} onCheckedChange={handleChangeAgree} />
					<div>
						<Link
							to={PATHS.POLICY.SERVICE_POLICY}
							target="_blank"
							className="underline underline-offset-2"
							viewTransition
						>
							利用規約
						</Link>
						&thinsp; と &thinsp;
						<Link
							to={PATHS.POLICY.PRIVACY_POLICY}
							target="_blank"
							className="underline underline-offset-2"
							viewTransition
						>
							プライバシーポリシー
						</Link>
						&thinsp; を確認した上で同意します。
					</div>
				</div>
			</div>
		</AuthForm>
	);
};
